// Stage-based SLA thresholds (hours in the current stage before an order is flagged).
export const SLA_HOURS = {
  "Need to Call for Taxes": 24,
  "Tax Pending": 48,
  "Pending for Documents": 48,
  "ABS Order Quality/Final Review": 24,
};
export const SLA_DEFAULT_HOURS = 72;
const TERMINAL = ["Completed", "Cancelled", "Submitted"];

// When did the order enter its CURRENT status? Use the most recent history entry for that status.
export function stageEnteredAt(order) {
  const hist = order?.status_history || [];
  for (let i = hist.length - 1; i >= 0; i--) {
    if (hist[i].status === order.status && hist[i].timestamp) return hist[i].timestamp;
  }
  return order?.updated_at || order?.created_at || null;
}

export function slaInfo(order) {
  if (!order || TERMINAL.includes(order.status)) return { breached: false, hours: 0, threshold: 0, applicable: false };
  const entered = stageEnteredAt(order);
  if (!entered) return { breached: false, hours: 0, threshold: 0, applicable: false };
  const hours = (Date.now() - new Date(entered).getTime()) / 3.6e6;
  const threshold = SLA_HOURS[order.status] ?? SLA_DEFAULT_HOURS;
  return { breached: hours > threshold, hours, threshold, applicable: true };
}

export function humanizeDuration(ms) {
  if (ms == null || Number.isNaN(ms) || ms < 0) return "—";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (!d && m) parts.push(`${m}m`);
  return parts.join(" ") || "0m";
}

// Chronological history annotated with how long each stage lasted.
export function historyWithDurations(order) {
  const hist = order?.status_history || [];
  return hist.map((ev, idx) => {
    const start = new Date(ev.timestamp).getTime();
    const end = idx < hist.length - 1 ? new Date(hist[idx + 1].timestamp).getTime() : Date.now();
    return { ...ev, durationMs: Math.max(0, end - start), isCurrent: idx === hist.length - 1 };
  });
}

const ABSTRACTOR_STATUSES = ["Abstractor Order Received", "Abstractor Pending", "Clarification Needed"];
export function isAbstractorOrder(order) {
  return !!order && (ABSTRACTOR_STATUSES.includes(order.status) || !!order.assignment_package);
}

// SLA / countdown state for a ProTitleUSA (abstractor) order.
// - "awaiting": assigned, no reply yet -> elapsed since assignment.
// - "due": reply captured with an ETA due date -> remaining time (or overdue).
export function abstractorSla(order) {
  if (!isAbstractorOrder(order)) return null;
  const reply = order.abstractor_reply;
  const now = Date.now();
  if (reply?.eta_due) {
    const due = new Date(reply.eta_due).getTime();
    if (!Number.isNaN(due)) {
      const remainMs = due - now;
      const overdue = remainMs < 0;
      const soon = !overdue && remainMs < 24 * 3.6e6;
      return {
        kind: "due", overdue, soon, dueAt: reply.eta_due,
        label: overdue ? `ETA overdue by ${humanizeDuration(-remainMs)}` : `ETA due in ${humanizeDuration(remainMs)}`,
        level: overdue ? "red" : soon ? "amber" : "green",
        decision: reply.decision || "",
      };
    }
  }
  if (reply) {
    return { kind: "replied", label: "Reply received", level: "green", decision: reply.decision || "" };
  }
  // Awaiting the abstractor's reply — count from when the package was generated / status set.
  const since = order.assignment_package?.generated_at || stageEnteredAt(order);
  if (!since) return null;
  const elapsed = now - new Date(since).getTime();
  const stale = elapsed > 24 * 3.6e6;
  return {
    kind: "awaiting", stale,
    label: `Awaiting ProTitleUSA · ${humanizeDuration(elapsed)}`,
    level: stale ? "amber" : "slate",
  };
}

